// src/api/clubMembership.js

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

/**
 * Enhanced API functions for club membership management
 * Supports the new backend features for detailed rejection and removal
 */

/**
 * Reject a membership request with detailed information
 * @param {string} clubId - The club ID
 * @param {string} requestId - The membership request ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @param {Object} rejectionData - The rejection details
 * @param {string} rejectionData.reason - Required reason for rejection
 * @param {string} [rejectionData.category] - Optional rejection category
 * @param {boolean} [rejectionData.allowReapplication] - Whether to allow reapplication (default: true)
 * @param {string} [rejectionData.additionalNotes] - Optional additional notes
 * @returns {Promise<Object>} API response
 */
export const rejectMembershipRequest = async (clubId, requestId, adminUserId, rejectionData) => {
  try {
    const payload = {
      approvedBy: adminUserId,
      reason: rejectionData.reason,
      ...(rejectionData.category && { category: rejectionData.category }),
      allowReapplication: rejectionData.allowReapplication !== undefined ? rejectionData.allowReapplication : true,
      ...(rejectionData.additionalNotes && { additionalNotes: rejectionData.additionalNotes })
    };

    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/reject-request/${requestId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to reject membership request');
    }

    return {
      success: true,
      data,
      message: data.message || 'Membership request rejected successfully'
    };
  } catch (error) {
    console.error('Error rejecting membership request:', error);
    const err = new Error(error.message || 'Failed to reject membership request');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Remove a club member with detailed information
 * @param {string} clubId - The club ID
 * @param {string} memberId - The member ID to remove
 * @param {string} adminUserId - The admin user ID performing the action
 * @param {Object} removalData - The removal details
 * @param {string} removalData.reason - Required reason for removal
 * @param {string} [removalData.removalType] - Optional removal type (default: "other")
 * @param {string} [removalData.effectiveDate] - Optional effective date (ISO string)
 * @param {boolean} [removalData.notifyMember] - Whether to notify the member (default: true)
 * @param {string} [removalData.additionalNotes] - Optional additional notes
 * @returns {Promise<Object>} API response
 */
export const removeMember = async (clubId, memberId, adminUserId, removalData) => {
  try {
    const payload = {
      removedBy: adminUserId,
      reason: removalData.reason,
      ...(removalData.removalType && { removalType: removalData.removalType }),
      ...(removalData.effectiveDate && { effectiveDate: removalData.effectiveDate }),
      notifyMember: removalData.notifyMember !== undefined ? removalData.notifyMember : true,
      ...(removalData.additionalNotes && { additionalNotes: removalData.additionalNotes })
    };

    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/remove-member/${memberId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to remove member');
    }

    return {
      success: true,
      data,
      message: data.message || 'Member removed successfully'
    };
  } catch (error) {
    console.error('Error removing member:', error);
    const err = new Error(error.message || 'Failed to remove member');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Legacy function for simple rejection (backward compatibility)
 * @param {string} clubId - The club ID
 * @param {string} requestId - The membership request ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @param {string} reason - Simple reason for rejection
 * @returns {Promise<Object>} API response
 */
export const rejectMembershipRequestSimple = async (clubId, requestId, adminUserId, reason = 'Request rejected') => {
  return rejectMembershipRequest(clubId, requestId, adminUserId, {
    reason,
    allowReapplication: true
  });
};

/**
 * Legacy function for simple member removal (backward compatibility)
 * @param {string} clubId - The club ID
 * @param {string} memberId - The member ID to remove
 * @param {string} adminUserId - The admin user ID performing the action
 * @param {string} reason - Simple reason for removal
 * @returns {Promise<Object>} API response
 */
export const removeMemberSimple = async (clubId, memberId, adminUserId, reason = 'Member removed') => {
  return removeMember(clubId, memberId, adminUserId, {
    reason,
    notifyMember: true
  });
};

/**
 * Approve a membership request (existing functionality)
 * @param {string} clubId - The club ID
 * @param {string} requestId - The membership request ID
 * @param {string} adminUserId - The admin user ID performing the action
 * @returns {Promise<Object>} API response
 */
export const approveMembershipRequest = async (clubId, requestId, adminUserId) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/approve-request/${requestId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        approvedBy: adminUserId,
      }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to approve membership request');
    }

    return {
      success: true,
      data,
      message: data.message || 'Membership request approved successfully'
    };
  } catch (error) {
    console.error('Error approving membership request:', error);
    const err = new Error(error.message || 'Failed to approve membership request');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Get club membership data (members and pending requests)
 * @param {string} clubId - The club ID
 * @returns {Promise<Object>} API response with members and pending requests
 */
export const getClubMembershipData = async (clubId) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/membership-data`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch membership data');
    }

    return {
      success: true,
      data: {
        members: data.members || [],
        pendingRequests: data.pendingRequests || []
      }
    };
  } catch (error) {
    console.error('Error fetching membership data:', error);
    const err = new Error(error.message || 'Failed to fetch membership data');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Constants for rejection categories and removal types
 */
export const REJECTION_CATEGORIES = [
  'incomplete_application',
  'does_not_meet_requirements', 
  'club_full',
  'skill_level_mismatch',
  'behavioral_concerns',
  'other'
];

export const REMOVAL_TYPES = [
  'violation',      // Policy Violation
  'inactivity',     // Inactivity  
  'request',        // Member Request
  'restructuring',  // Club Restructuring
  'non_payment',    // Non-Payment
  'other'          // Other
];

/**
 * Helper function to format rejection category for display
 * @param {string} category - The rejection category
 * @returns {string} Formatted category name
 */
export const formatRejectionCategory = (category) => {
  const categoryMap = {
    'incomplete_application': 'Incomplete Application',
    'does_not_meet_requirements': 'Does Not Meet Requirements',
    'club_full': 'Club Full',
    'skill_level_mismatch': 'Skill Level Mismatch',
    'behavioral_concerns': 'Behavioral Concerns',
    'other': 'Other'
  };
  return categoryMap[category] || category;
};

/**
 * Helper function to format removal type for display
 * @param {string} type - The removal type
 * @returns {string} Formatted type name
 */
export const formatRemovalType = (type) => {
  const typeMap = {
    'violation': 'Policy Violation',
    'inactivity': 'Inactivity',
    'request': 'Member Request',
    'restructuring': 'Club Restructuring',
    'non_payment': 'Non-Payment',
    'other': 'Other'
  };
  return typeMap[type] || type;
};

// ============================================================================
// NEW ENHANCED API FUNCTIONS (December 2024)
// ============================================================================

/**
 * Get members with search, filter, sort, and pagination
 * @param {string} clubId - The club ID
 * @param {Object} options - Query options
 * @param {string} [options.search] - Search by name or email
 * @param {string} [options.role] - Filter by role
 * @param {string} [options.sort] - Sort field (name, email, role, joinedAt)
 * @param {string} [options.order] - Sort order (asc, desc)
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Items per page
 * @returns {Promise<Object>} API response with members, pagination, and stats
 */
export const getMembers = async (clubId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.search) params.append('search', options.search);
    if (options.role) params.append('role', options.role);
    if (options.sort) params.append('sort', options.sort);
    if (options.order) params.append('order', options.order);
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);

    const url = `${backendUrl}/api/clubs/${clubId}/members${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch members');
    }

    return {
      success: true,
      members: data.members || data || [],
      pagination: data.pagination || null,
      stats: data.stats || null
    };
  } catch (error) {
    console.error('Error fetching members:', error);
    const err = new Error(error.message || 'Failed to fetch members');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Update a member's role
 * @param {string} clubId - The club ID
 * @param {string} memberId - The member ID
 * @param {string} role - The new role
 * @param {string} updatedBy - The user ID performing the update
 * @returns {Promise<Object>} API response
 */
export const updateMemberRole = async (clubId, memberId, role, updatedBy) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/members/${memberId}/role`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ role, updatedBy }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to update member role');
    }

    return {
      success: true,
      data,
      message: data.message || `Role updated to ${role}`
    };
  } catch (error) {
    console.error('Error updating member role:', error);
    const err = new Error(error.message || 'Failed to update member role');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Bulk approve pending requests
 * @param {string} clubId - The club ID
 * @param {string[]} requestIds - Array of request IDs to approve
 * @param {string} approvedBy - The user ID performing the approval
 * @returns {Promise<Object>} API response
 */
export const bulkApproveRequests = async (clubId, requestIds, approvedBy) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/pending-requests/bulk-approve`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requestIds, approvedBy }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to bulk approve requests');
    }

    return {
      success: true,
      data,
      message: data.message || `${requestIds.length} requests approved`
    };
  } catch (error) {
    console.error('Error bulk approving requests:', error);
    const err = new Error(error.message || 'Failed to bulk approve requests');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Bulk reject pending requests
 * @param {string} clubId - The club ID
 * @param {string[]} requestIds - Array of request IDs to reject
 * @param {string} rejectedBy - The user ID performing the rejection
 * @param {string} [reason] - Optional reason for rejection
 * @returns {Promise<Object>} API response
 */
export const bulkRejectRequests = async (clubId, requestIds, rejectedBy, reason = '') => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/pending-requests/bulk-reject`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ requestIds, rejectedBy, reason }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to bulk reject requests');
    }

    return {
      success: true,
      data,
      message: data.message || `${requestIds.length} requests rejected`
    };
  } catch (error) {
    console.error('Error bulk rejecting requests:', error);
    const err = new Error(error.message || 'Failed to bulk reject requests');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Create an invite link
 * @param {string} clubId - The club ID
 * @param {Object} options - Invite options
 * @param {string} options.role - Role for invited members
 * @param {string} [options.expiresIn] - Expiration time (e.g., '7d')
 * @param {number} [options.maxUses] - Maximum number of uses
 * @param {string} options.createdBy - The user ID creating the link
 * @returns {Promise<Object>} API response with invite link
 */
export const createInviteLink = async (clubId, options) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/invite-link`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(options),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to create invite link');
    }

    return {
      success: true,
      inviteLink: data.inviteLink,
      code: data.code,
      expiresAt: data.expiresAt,
      maxUses: data.maxUses
    };
  } catch (error) {
    console.error('Error creating invite link:', error);
    const err = new Error(error.message || 'Failed to create invite link');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Get all invite links for a club
 * @param {string} clubId - The club ID
 * @returns {Promise<Object>} API response with invite links
 */
export const getInviteLinks = async (clubId) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/invite-links`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch invite links');
    }

    return {
      success: true,
      inviteLinks: data.inviteLinks || data || []
    };
  } catch (error) {
    console.error('Error fetching invite links:', error);
    const err = new Error(error.message || 'Failed to fetch invite links');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Revoke an invite link
 * @param {string} clubId - The club ID
 * @param {string} linkId - The invite link ID
 * @param {string} revokedBy - The user ID revoking the link
 * @returns {Promise<Object>} API response
 */
export const revokeInviteLink = async (clubId, linkId, revokedBy) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/invite-links/${linkId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ revokedBy }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to revoke invite link');
    }

    return {
      success: true,
      message: data.message || 'Invite link revoked'
    };
  } catch (error) {
    console.error('Error revoking invite link:', error);
    const err = new Error(error.message || 'Failed to revoke invite link');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Get activity log for a club
 * @param {string} clubId - The club ID
 * @param {Object} options - Query options
 * @param {string} [options.type] - Filter by activity type
 * @param {number} [options.limit] - Limit number of results
 * @param {number} [options.page] - Page number
 * @returns {Promise<Object>} API response with activities
 */
export const getActivityLog = async (clubId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.type) params.append('type', options.type);
    if (options.limit) params.append('limit', options.limit);
    if (options.page) params.append('page', options.page);

    const url = `${backendUrl}/api/clubs/${clubId}/activity-log${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch activity log');
    }

    return {
      success: true,
      activities: data.activities || data || [],
      pagination: data.pagination || null
    };
  } catch (error) {
    console.error('Error fetching activity log:', error);
    const err = new Error(error.message || 'Failed to fetch activity log');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Export members list
 * @param {string} clubId - The club ID
 * @param {string} format - Export format (csv, json)
 * @returns {Promise<Blob>} File blob for download
 */
export const exportMembers = async (clubId, format = 'csv') => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/members/export?format=${format}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.message || 'Failed to export members');
    }

    const blob = await response.blob();
    return blob;
  } catch (error) {
    console.error('Error exporting members:', error);
    const err = new Error(error.message || 'Failed to export members');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Join club via invite code
 * @param {string} code - The invite code
 * @param {string} userId - The user ID joining
 * @returns {Promise<Object>} API response
 */
export const joinViaInviteCode = async (code, userId) => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/join/${code}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to join club');
    }

    return {
      success: true,
      data,
      message: data.message || 'Successfully joined club'
    };
  } catch (error) {
    console.error('Error joining via invite code:', error);
    const err = new Error(error.message || 'Failed to join club');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

// ============================================================================
// USER SEARCH & INVITATION API FUNCTIONS (December 2024)
// ============================================================================

/**
 * Search users by name or email
 * @param {Object} options - Search options
 * @param {string} options.q - Search query (min 2 chars)
 * @param {string} [options.excludeClub] - Exclude members of this club
 * @param {string} [options.sport] - Filter by sport
 * @param {string} [options.city] - Filter by city
 * @param {number} [options.limit] - Max results
 * @param {number} [options.page] - Page number
 * @returns {Promise<Object>} API response with users
 */
export const searchUsers = async (options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.q) params.append('q', options.q);
    if (options.excludeClub) params.append('excludeClub', options.excludeClub);
    if (options.sport) params.append('sport', options.sport);
    if (options.city) params.append('city', options.city);
    if (options.limit) params.append('limit', options.limit);
    if (options.page) params.append('page', options.page);

    const url = `${backendUrl}/api/users/search?${params.toString()}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to search users');
    }

    return {
      success: true,
      users: data.users || [],
      pagination: data.pagination || null
    };
  } catch (error) {
    console.error('Error searching users:', error);
    const err = new Error(error.message || 'Failed to search users');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Bulk invite users to a club
 * @param {string} clubId - The club ID
 * @param {string[]} userIds - Array of user IDs to invite
 * @param {string} role - Role to assign (Member, Manager)
 * @param {string} invitedBy - User ID of the inviter
 * @param {string} [message] - Optional personal message
 * @returns {Promise<Object>} API response with results
 */
export const inviteUsersToClub = async (clubId, userIds, role, invitedBy, message = '') => {
  try {
    const response = await fetch(`${backendUrl}/api/clubs/${clubId}/invite-users`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userIds, role, invitedBy, message }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to invite users');
    }

    return {
      success: true,
      message: data.message,
      results: data.results
    };
  } catch (error) {
    console.error('Error inviting users:', error);
    const err = new Error(error.message || 'Failed to invite users');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Get user's pending club invitations
 * @param {string} userId - The user ID
 * @returns {Promise<Object>} API response with invitations
 */
export const getUserInvitations = async (userId) => {
  try {
    const response = await fetch(`${backendUrl}/api/users/${userId}/club-invitations`);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch invitations');
    }

    return {
      success: true,
      invitations: data.invitations || []
    };
  } catch (error) {
    console.error('Error fetching invitations:', error);
    const err = new Error(error.message || 'Failed to fetch invitations');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Respond to a club invitation
 * @param {string} invitationId - The invitation ID
 * @param {string} action - 'accept' or 'decline'
 * @param {string} userId - The user ID responding
 * @returns {Promise<Object>} API response
 */
export const respondToInvitation = async (invitationId, action, userId) => {
  try {
    const response = await fetch(`${backendUrl}/api/club-invitations/respond/${invitationId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action, userId }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to respond to invitation');
    }

    return {
      success: true,
      message: data.message,
      action: data.action,
      club: data.club
    };
  } catch (error) {
    console.error('Error responding to invitation:', error);
    const err = new Error(error.message || 'Failed to respond to invitation');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Get club's sent invitations
 * @param {string} clubId - The club ID
 * @param {Object} options - Query options
 * @param {string} [options.status] - Filter by status
 * @param {number} [options.page] - Page number
 * @param {number} [options.limit] - Items per page
 * @returns {Promise<Object>} API response with invitations
 */
export const getClubInvitations = async (clubId, options = {}) => {
  try {
    const params = new URLSearchParams();
    if (options.status) params.append('status', options.status);
    if (options.page) params.append('page', options.page);
    if (options.limit) params.append('limit', options.limit);

    const url = `${backendUrl}/api/clubs/${clubId}/invitations${params.toString() ? '?' + params.toString() : ''}`;
    const response = await fetch(url);
    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to fetch club invitations');
    }

    return {
      success: true,
      invitations: data.invitations || [],
      pagination: data.pagination || null
    };
  } catch (error) {
    console.error('Error fetching club invitations:', error);
    const err = new Error(error.message || 'Failed to fetch club invitations');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};

/**
 * Cancel a sent invitation
 * @param {string} invitationId - The invitation ID
 * @param {string} cancelledBy - User ID cancelling the invitation
 * @returns {Promise<Object>} API response
 */
export const cancelInvitation = async (invitationId, cancelledBy) => {
  try {
    const response = await fetch(`${backendUrl}/api/club-invitations/${invitationId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ cancelledBy }),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.message || 'Failed to cancel invitation');
    }

    return {
      success: true,
      message: data.message || 'Invitation cancelled'
    };
  } catch (error) {
    console.error('Error cancelling invitation:', error);
    const err = new Error(error.message || 'Failed to cancel invitation');
    err.success = false;
    err.originalError = error;
    throw err;
  }
};


